import React, { useState, useEffect, useRef, useId } from 'react';
import { getMediaUrl } from '../utils/presignedUrls';
import { Loader2 } from 'lucide-react';
import { useVideo } from '../context/VideoContext';

/**
 * Image component that handles presigned URLs automatically.
 * Fetches presigned URL if needed and displays the image.
 */
export function PresignedImage({ src, alt, className, ...props }) {
  const [resolvedUrl, setResolvedUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    
    async function loadUrl() {
      if (!src) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(false);
        const url = await getMediaUrl(src);
        if (mounted) {
          setResolvedUrl(url);
        }
      } catch (err) {
        console.error('Failed to load presigned URL:', err);
        if (mounted) {
          setError(true);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadUrl();
    return () => { mounted = false; };
  }, [src]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-gray-800 ${className}`} {...props}>
        <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error || !resolvedUrl) {
    return (
      <div className={`flex items-center justify-center bg-gray-800 ${className}`} {...props}>
        <span className="text-gray-500 text-sm">Failed to load</span>
      </div>
    );
  }

  return (
    <img 
      src={resolvedUrl} 
      alt={alt} 
      className={className} 
      onError={() => setError(true)}
      {...props} 
    />
  );
}

/**
 * Video component that handles presigned URLs automatically.
 * Fetches presigned URL if needed and displays the video.
 */
export const PresignedVideo = React.forwardRef(function PresignedVideo({ src, className, controls = true, ...props }, ref) {
  const [resolvedUrl, setResolvedUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    
    async function loadUrl() {
      if (!src) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(false);
        const url = await getMediaUrl(src);
        if (mounted) {
          setResolvedUrl(url);
        }
      } catch (err) {
        console.error('Failed to load presigned URL:', err);
        if (mounted) {
          setError(true);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadUrl();
    return () => { mounted = false; };
  }, [src]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-gray-800 ${className}`} {...props}>
        <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error || !resolvedUrl) {
    return (
      <div className={`flex items-center justify-center bg-gray-800 ${className}`} {...props}>
        <span className="text-gray-500 text-sm">Failed to load</span>
      </div>
    );
  }

  return (
    <video 
      ref={ref}
      src={resolvedUrl} 
      className={className} 
      controls={controls}
      onError={() => setError(true)}
      {...props} 
    />
  );
});

/**
 * Lazy video component that shows thumbnail first and only loads video on play.
 * Optimizes data usage by not loading video until user requests it.
 * Supports mutual exclusion - only one video plays at a time.
 */
export function LazyVideo({ src, thumbnailSrc, className, ...props }) {
  const videoId = useId();
  const { currentlyPlaying, registerPlaying } = useVideo();
  const [showVideo, setShowVideo] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [thumbnailLoading, setThumbnailLoading] = useState(true);
  const [videoLoading, setVideoLoading] = useState(false);
  const [error, setError] = useState(false);
  const [usePosterFallback, setUsePosterFallback] = useState(false);
  const videoRef = useRef(null);
  const posterVideoRef = useRef(null);

  // Pause this video when another video starts playing
  useEffect(() => {
    if (currentlyPlaying && currentlyPlaying !== videoId && videoRef.current) {
      videoRef.current.pause();
    }
  }, [currentlyPlaying, videoId]);

  // Check if thumbnail is actually different from video
  const hasDedicatedThumbnail = thumbnailSrc && thumbnailSrc !== src && !thumbnailSrc.includes(src?.split('/').pop()?.split('.')[0] || 'never-match');

  // Load thumbnail URL on mount
  useEffect(() => {
    let mounted = true;
    
    async function loadThumbnail() {
      // If no dedicated thumbnail, use poster fallback approach
      if (!hasDedicatedThumbnail) {
        setUsePosterFallback(true);
        setThumbnailLoading(false);
        return;
      }
      
      try {
        setThumbnailLoading(true);
        const url = await getMediaUrl(thumbnailSrc);
        if (mounted) {
          setThumbnailUrl(url);
        }
      } catch (err) {
        console.error('Failed to load thumbnail URL:', err);
        // Fall back to poster approach
        setUsePosterFallback(true);
      } finally {
        if (mounted) {
          setThumbnailLoading(false);
        }
      }
    }

    loadThumbnail();
    return () => { mounted = false; };
  }, [thumbnailSrc, hasDedicatedThumbnail]);

  // For poster fallback, load video metadata only
  useEffect(() => {
    let mounted = true;
    
    async function loadPosterVideo() {
      if (!usePosterFallback || !src) return;
      
      try {
        const url = await getMediaUrl(src);
        if (mounted) {
          setVideoUrl(url);
        }
      } catch (err) {
        console.error('Failed to load video URL for poster:', err);
        if (mounted) {
          setError(true);
        }
      }
    }

    loadPosterVideo();
    return () => { mounted = false; };
  }, [usePosterFallback, src]);

  // Load and play video when user clicks play
  const handlePlay = async () => {
    // Register this video as currently playing (will pause others)
    registerPlaying(videoId);
    
    if (showVideo && videoUrl) {
      // Video already loaded, just play
      if (videoRef.current) {
        videoRef.current.play();
      }
      return;
    }

    try {
      setVideoLoading(true);
      if (!videoUrl) {
        const url = await getMediaUrl(src);
        setVideoUrl(url);
      }
      setShowVideo(true);
      // Video will autoplay after load
    } catch (err) {
      console.error('Failed to load video URL:', err);
      setError(true);
    } finally {
      setVideoLoading(false);
    }
  };

  // Auto-play when video is ready
  useEffect(() => {
    if (showVideo && videoUrl && videoRef.current) {
      registerPlaying(videoId);
      videoRef.current.play().catch(err => {
        console.log('Autoplay prevented:', err);
      });
    }
  }, [showVideo, videoUrl, videoId, registerPlaying]);

  if (thumbnailLoading) {
    return (
      <div className={`flex items-center justify-center bg-gray-800 ${className}`} {...props}>
        <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-gray-800 ${className}`} {...props}>
        <span className="text-gray-500 text-sm">Failed to load</span>
      </div>
    );
  }

  // Show video player once loaded
  if (showVideo && videoUrl) {
    return (
      <video 
        ref={videoRef}
        src={videoUrl} 
        className={className} 
        controls
        onError={() => setError(true)}
        {...props} 
      />
    );
  }

  // For poster fallback mode - show video paused at first frame with overlay
  if (usePosterFallback && videoUrl) {
    return (
      <div 
        className={`relative cursor-pointer group ${className}`} 
        onClick={handlePlay}
        {...props}
      >
        <video 
          ref={posterVideoRef}
          src={videoUrl}
          className="w-full h-full object-cover"
          preload="metadata"
          muted
          playsInline
          onError={() => setError(true)}
        />
        
        {/* Play button overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
          {videoLoading ? (
            <Loader2 className="w-12 h-12 text-white animate-spin" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-white/90 group-hover:bg-white flex items-center justify-center transition-colors shadow-lg">
              <svg 
                className="w-8 h-8 text-gray-900 ml-1" 
                fill="currentColor" 
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z"/>
              </svg>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show thumbnail with play button (dedicated thumbnail mode)
  return (
    <div 
      className={`relative cursor-pointer group ${className}`} 
      onClick={handlePlay}
      {...props}
    >
      {thumbnailUrl ? (
        <img 
          src={thumbnailUrl} 
          alt="Video thumbnail"
          className="w-full h-full object-cover"
          onError={() => setUsePosterFallback(true)}
        />
      ) : (
        <div className="w-full h-full bg-gray-800 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
        </div>
      )}
      
      {/* Play button overlay */}
      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
        {videoLoading ? (
          <Loader2 className="w-12 h-12 text-white animate-spin" />
        ) : (
          <div className="w-16 h-16 rounded-full bg-white/90 group-hover:bg-white flex items-center justify-center transition-colors shadow-lg">
            <svg 
              className="w-8 h-8 text-gray-900 ml-1" 
              fill="currentColor" 
              viewBox="0 0 24 24"
            >
              <path d="M8 5v14l11-7z"/>
            </svg>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Generic media component that auto-detects type.
 * Renders PresignedImage for images and PresignedVideo for videos.
 */
export function PresignedMedia({ src, mediaType, alt, className, ...props }) {
  if (mediaType === 'video') {
    return <PresignedVideo src={src} className={className} {...props} />;
  }
  return <PresignedImage src={src} alt={alt} className={className} {...props} />;
}

export default PresignedMedia;
